import React, { memo } from "react"
import { shallowEqual } from "react-redux"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import { RootState } from "../../../store/rootReducer"
import { selectCatalog, selectCurrentBranch } from "../../../store/knowledgeBase/selectors"
import type { ItemId } from "@atlaskit/tree/types"
import { clearCurrentArticle, mutateCurrentBranch, toggleCategoryExpanded } from "../../../store/knowledgeBase/slice"
import { push } from "connected-react-router"
import { buildSharedArticleActionUrl } from "../../../utility/knowledgeBase/articleUrlHelper"
import { getSharedCategories } from "../../../store/knowledgeBase/thunks/shared"
import type { TreeItem } from "@atlaskit/tree"
import LazyCatalog from "../LazyCatalog"

interface SharedCatalogProps {
    id: string
    url: string
    shareId: string
}

const SharedCatalog: React.FC<SharedCatalogProps> = props => {
    const { url, id, shareId } = props
    const dispatch = useAppDispatch()
    const tree = useAppSelector((state: RootState) => selectCatalog(state, id), shallowEqual)
    const { CurrentCategoryId } = useAppSelector(selectCurrentBranch, shallowEqual)

    const handleCategoryTitleClick = (categoryItem: TreeItem, catalogCode?: string, categoryCode?: string) => {
        if (categoryCode && categoryItem.id !== CurrentCategoryId) {
            dispatch(clearCurrentArticle())
            dispatch(
                mutateCurrentBranch({ CurrentCategoryId: categoryItem.id as string, CurrentCatalogCode: catalogCode })
            )
            dispatch(push(buildSharedArticleActionUrl(url, categoryCode)))
        }
    }

    const handleToggle = (itemId: ItemId, isExpanded: boolean) => {
        dispatch(
            toggleCategoryExpanded({
                CatalogId: tree.rootId,
                CategoryId: itemId,
                IsExpanded: isExpanded
            })
        )
    }

    const handleExpandCategory = (itemId: ItemId, onExpand: (itemId: ItemId) => void) => {
        const { children, isChildrenLoading } = tree.items[itemId]

        onExpand(itemId)

        if (!isChildrenLoading && children.length === 0) {
            dispatch(getSharedCategories(shareId, itemId, tree.rootId))
        }
    }

    const handleCanEdit = () => false

    return (
        <LazyCatalog
            tree={tree}
            onExpandCategory={handleExpandCategory}
            onTitleClick={handleCategoryTitleClick}
            onToggle={handleToggle}
            onCanEdit={handleCanEdit}
            isDragEnabled={false}
        />
    )
}

export default memo(SharedCatalog)
